import { useRef, useState } from "react";
import { KeyRound, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const OkxCredentialPanel = ({ data, busy, onSave, onRetest, onDelete,
  environment = "DEMO" }) => {
  const [editing, setEditing] = useState(false);
  const apiRef = useRef(null);
  const secretRef = useRef(null);
  const passphraseRef = useRef(null);
  const live = environment === "LIVE";
  const prefix = live ? "live-" : "";
  const configured = Boolean(data.credentials_configured);
  const showForm = !configured || editing;
  const submit = async (event) => {
    event.preventDefault();
    const credentials = {
      api_key: apiRef.current?.value || "",
      secret_key: secretRef.current?.value || "",
      passphrase: passphraseRef.current?.value || "",
    };
    const ok = await onSave(credentials);
    if (apiRef.current) apiRef.current.value = "";
    if (secretRef.current) secretRef.current.value = "";
    if (passphraseRef.current) passphraseRef.current.value = "";
    credentials.api_key = credentials.secret_key = credentials.passphrase = null;
    if (ok) setEditing(false);
  };
  return <section className={`credential-panel ph-no-capture ${live ? "live-credential-panel" : ""}`}
    data-ph-no-capture="true" data-testid={`${prefix}okx-credential-panel`}>
    <div className="section-heading">
      <div><p className="eyebrow">{live ? "真钱账户 · 默认停用" : "仅限模拟盘"}</p>
        <h2>连接 OKX {environment}</h2></div>
      <span className={`status-badge ${configured ? "ok" : "err"}`}
        data-testid={`${prefix}credential-panel-status`}>
        {configured ? "已安全配置" : "尚未配置"}
      </span>
    </div>
    {configured && !editing && <div className="credential-summary"
      data-testid={`${prefix}credential-masked-summary`}>
      <div><span>API Key</span><b className="mono"
        data-testid={`${prefix}masked-api-key`}>{data.api_key_masked}</b></div>
      <div><span>Secret Key</span><b data-testid={`${prefix}masked-secret-status`}>已配置</b></div>
      <div><span>Passphrase</span><b data-testid={`${prefix}masked-passphrase-status`}>已配置</b></div>
      <div className="credential-actions">
        <Button variant="secondary" onClick={onRetest} disabled={busy}
          data-testid={`${prefix}retest-okx-connection-button`}>
          <RefreshCw aria-hidden="true" />重新测试
        </Button>
        <Button variant="outline" onClick={() => setEditing(true)} disabled={busy}
          data-testid={`${prefix}update-okx-api-button`}>
          <KeyRound aria-hidden="true" />更新 API 凭证
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="destructive" disabled={busy}
            data-testid={`${prefix}delete-okx-api-button`}>
            <Trash2 aria-hidden="true" />删除 OKX {environment} API
          </Button></AlertDialogTrigger>
          <AlertDialogContent data-testid={`${prefix}delete-okx-api-dialog`}>
            <AlertDialogHeader><AlertDialogTitle data-testid={`${prefix}delete-okx-api-title`}>
              确认删除 {environment} API 凭证？
            </AlertDialogTitle><AlertDialogDescription
              data-testid={`${prefix}delete-okx-api-description`}>
              该环境将立即关闭。已有仓位不会被平仓，OKX 上现有保护单不会删除。
            </AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${prefix}cancel-delete-okx-api-button`}>取消</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete}
                data-testid={`${prefix}confirm-delete-okx-api-button`}>确认删除</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>}
    {showForm && <form className="credential-form" onSubmit={submit} autoComplete="off"
      data-ph-no-capture="true" data-testid={`${prefix}okx-credential-form`}>
      <label htmlFor={`${prefix}okx-api-key`}>API Key<input id={`${prefix}okx-api-key`}
        ref={apiRef} type="password" required autoComplete="new-password" data-lpignore="true"
        data-1p-ignore="true" data-ph-no-capture="true" data-testid={`${prefix}okx-api-key-input`} /></label>
      <label htmlFor={`${prefix}okx-secret-key`}>Secret Key<input id={`${prefix}okx-secret-key`}
        ref={secretRef} type="password" required autoComplete="new-password" data-lpignore="true"
        data-1p-ignore="true" data-ph-no-capture="true" data-testid={`${prefix}okx-secret-key-input`} /></label>
      <label htmlFor={`${prefix}okx-passphrase`}>Passphrase<input id={`${prefix}okx-passphrase`}
        ref={passphraseRef} type="password" required autoComplete="new-password" data-lpignore="true"
        data-1p-ignore="true" data-ph-no-capture="true" data-testid={`${prefix}okx-passphrase-input`} /></label>
      <div className="credential-form-actions">
        {configured && <Button type="button" variant="ghost" onClick={() => setEditing(false)}
          data-testid={`${prefix}cancel-update-okx-api-button`}>取消</Button>}
        <Button type="submit" disabled={busy} data-testid={`${prefix}save-test-okx-api-button`}>
          <ShieldCheck aria-hidden="true" />保存并测试连接
        </Button>
      </div>
    </form>}
    <p className="credential-privacy" data-testid={`${prefix}credential-privacy-note`}>
      凭证经 HTTPS 提交并由服务器加密保存；页面刷新后无法读取 Secret Key 或 Passphrase。
      {live ? " Live 只允许 Read + Trade，不需要且不接受 Withdraw 权限。" : ""}
    </p>
  </section>;
};